/*
 * Quick add.
 *
 * A product card with size or colour options still reads "Add to Cart", because
 * that is what the shopper is trying to do. Pressing it opens this dialog,
 * which asks only the questions that product actually has, then posts to the
 * cart. Nothing is added blind, and nobody is bounced to the product page.
 *
 * Each card carries its own variant JSON beside its button, so no extra request
 * is needed. Exactly one dialog is built for the page, lazily on first use:
 * lts2-head renders once per section, so any markup with an id would repeat.
 *
 * The cart request goes to Shopify's own locale-aware root, so the selected
 * market and currency survive the add.
 */
(function () {
  'use strict';

  /* lts2-head renders once per section, so this file arrives in several script
     tags on one page. The browser caches the fetch but executes each tag, and
     each execution would register its own listeners - nine delegated click
     handlers and nine dialogs. First one in wins; the rest return. */
  if (window.__lts2QuickAddReady) return;
  window.__lts2QuickAddReady = true;

  var dialog = null;
  var T = null;
  var state = { data: null, chosen: [], trigger: null };

  function strings() {
    var el = document.querySelector('[data-lts-qa-i18n]');
    try { return el ? JSON.parse(el.textContent) : {}; } catch (e) { return {}; }
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function focusables(scope) {
    return Array.prototype.slice.call(scope.querySelectorAll(
      'button:not([disabled]), [href], input, select, [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
  }

  function trapTab(e, scope) {
    var items = focusables(scope);
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function build() {
    var d = document.createElement('div');
    d.className = 'lts2-qa';
    d.hidden = true;
    d.innerHTML =
      '<div class="lts2-qa__backdrop" data-backdrop></div>' +
      '<div class="lts2-qa__panel" role="dialog" aria-modal="true" aria-labelledby="lts2-qa-title">' +
        '<div class="lts2-qa__head">' +
          '<p class="lts2-qa__title" id="lts2-qa-title" data-title></p>' +
          '<button type="button" class="lts2-qa__close" data-close>' +
            '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true"><path d="M3.5 3.5 12.5 12.5M12.5 3.5 3.5 12.5"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="lts2-qa__body" data-body></div>' +
        '<p class="lts2-qa__status" role="status" aria-live="polite" data-status></p>' +
        '<button type="button" class="button button-ATC lts2-qa__submit" data-submit></button>' +
      '</div>';
    document.body.appendChild(d);

    d.querySelector('[data-close]').setAttribute('aria-label', T.close || 'Close');
    d.querySelector('[data-backdrop]').addEventListener('click', function () { close(true); });
    d.querySelector('[data-close]').addEventListener('click', function () { close(true); });
    d.querySelector('[data-submit]').addEventListener('click', submit);
    d.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(true); }
      else if (e.key === 'Tab') trapTab(e, d);
    });
    return d;
  }

  /* The variant whose option list matches every current choice. */
  function match() {
    if (!state.data) return null;
    if (!state.chosen.every(function (c) { return c !== null; })) return null;
    for (var i = 0; i < state.data.variants.length; i++) {
      var v = state.data.variants[i], hit = true;
      for (var j = 0; j < state.chosen.length; j++) {
        if (v.options[j] !== state.chosen[j]) { hit = false; break; }
      }
      if (hit) return v;
    }
    return null;
  }

  /* A value is offered only when some in-stock variant carries it alongside the
     choices already made, so dead combinations are visibly unavailable. */
  function valueAvailable(pos, value) {
    return state.data.variants.some(function (v) {
      if (v.options[pos] !== value || !v.available) return false;
      return state.chosen.every(function (c, k) { return k === pos || c === null || v.options[k] === c; });
    });
  }

  function render() {
    var body = dialog.querySelector('[data-body]');
    body.innerHTML = '';
    state.data.options.forEach(function (opt, pos) {
      var group = document.createElement('fieldset');
      group.className = 'lts2-qa__group';

      var legend = document.createElement('legend');
      legend.className = 'lts2-qa__legend';
      legend.textContent = (T.select || 'Select') + ' ' + opt.name;
      group.appendChild(legend);

      var row = document.createElement('div');
      row.className = 'lts2-qa__values';
      opt.values.forEach(function (value) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'lts2-qa__value';
        b.textContent = value;
        if (!valueAvailable(pos, value)) {
          b.disabled = true;
          b.setAttribute('aria-disabled', 'true');
          b.setAttribute('title', T.unavailable || 'Unavailable');
        }
        var picked = state.chosen[pos] === value;
        if (picked) b.classList.add('is-selected');
        b.setAttribute('aria-pressed', picked ? 'true' : 'false');
        b.addEventListener('click', function () {
          state.chosen[pos] = picked ? null : value;
          render();
        });
        row.appendChild(b);
      });

      /* Made to measure needs the product page, where the measurement dialog
         writes into the real product form; #custom-size opens it there. */
      if (state.data.customSize && /^size$/i.test(opt.name) && state.data.url) {
        var cs = document.createElement('a');
        cs.className = 'lts2-qa__value lts2-qa__custom';
        cs.href = state.data.url + '#custom-size';
        cs.textContent = T.custom_size || 'Custom size';
        cs.title = T.custom_size_hint || '';
        row.appendChild(cs);
      }

      group.appendChild(row);
      body.appendChild(group);
    });
    syncSubmit();
  }

  function syncSubmit() {
    var btn = dialog.querySelector('[data-submit]');
    var v = match();
    btn.textContent = (v && !v.available) ? (T.sold_out || 'Sold out') : (T.add_to_cart || 'Add to cart');
    btn.disabled = !v || !v.available;
  }

  function submit() {
    var v = match();
    if (!v || !v.available) return;
    var btn = dialog.querySelector('[data-submit]');
    var status = dialog.querySelector('[data-status]');
    btn.disabled = true;
    status.textContent = T.adding || 'Adding...';

    fetch(root().replace(/\/$/, '') + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () {
        status.textContent = T.added || 'Added to cart';
        document.dispatchEvent(new CustomEvent('lts2:cart:added'));
        var drawer = document.querySelector('[data-cart-sidebar]');
        setTimeout(function () {
          close(true);
          if (drawer) drawer.click(); else window.location.reload();
        }, 550);
      })
      .catch(function () {
        status.textContent = T.error || 'Could not add to cart';
        btn.disabled = false;
      });
  }

  function open(card, trigger) {
    var holder = card.querySelector('[data-lts-qa-data]');
    if (!holder) return;
    var data;
    try { data = JSON.parse(holder.textContent); } catch (e) { return; }

    T = T || strings();
    dialog = dialog || build();
    state.data = data;
    state.trigger = trigger;
    state.chosen = data.options.map(function () { return null; });

    dialog.querySelector('[data-title]').textContent = data.title;
    dialog.querySelector('[data-status]').textContent = '';
    render();

    dialog.hidden = false;
    requestAnimationFrame(function () { dialog.classList.add('is-open'); });
    document.body.classList.add('lts2-qa-locked');
    var first = focusables(dialog)[0];
    if (first) first.focus();
  }

  function close(returnFocus) {
    if (!dialog || dialog.hidden) return;
    dialog.classList.remove('is-open');
    document.body.classList.remove('lts2-qa-locked');
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var finish = function () { dialog.hidden = true; };
    if (reduced) finish(); else setTimeout(finish, 200);
    if (returnFocus && state.trigger) state.trigger.focus();
  }

  /* Delegated, so cards swapped in by the Section Rendering API work with no
     re-init and no duplicate listeners. */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest ? e.target.closest('[data-lts-qa]') : null;
    if (!trigger) return;
    var card = trigger.closest('.lts2-pcard');
    if (!card) return;
    e.preventDefault();
    open(card, trigger);
  });
})();
